// CommunicationDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { TimeSelect } from '../../ui/TimeSelect';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { activityAPI, hospitalityAPI, marketingAPI } from '../../../services/api';
import { Pencil, AlertCircle, Calendar, User, Link2, Clock, Sparkles, Trash2, Mail, MessageSquare, CheckCircle2, Upload, Camera, DollarSign, FileText, Gift, Users, Star, Building2, Download, EyeOff } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { associationAPI } from '../../../services/api';
import api from '../../../services/api';
import { TaskDialog } from './TaskDialog';

export const CommunicationDialog = ({ open, onOpenChange, accountId, horseId = null, onSuccess, associations = [], users = [], contactEntries = [], tasks = [], initialData = null, accounts = [], horses = [], showAccountDropdown = false }) => {
  const [loading, setLoading] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [filteredAssociations, setFilteredAssociations] = useState([]);
  const [dynamicContactEntries, setDynamicContactEntries] = useState([]);
  const [formData, setFormData] = useState({
    activity_name: '',
    description: '',
    communication_method: 'telephone',
    start_date_time: new Date().toISOString().split('T')[0],
    association_id: '',
    horse_id: '',
    assigned_to_user_id: '',
    contact_entry_id: '',
    is_sensitive: false,
    linked_task_ids: [],
    linked_account_id: '',
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData && open) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        start_date_time: initialData.start_date_time || new Date().toISOString().split('T')[0],
      }));
    }
  }, [initialData, open]);

  // Fetch associations when account changes (for Horse context)
  useEffect(() => {
    const fetchAssociationsForAccount = async () => {
      if (showAccountDropdown && formData.linked_account_id) {
        try {
          const response = await associationAPI.list(formData.linked_account_id);
          const associations = response.data || [];
          
          // Enrich associations with entity names
          const enrichedAssociations = await Promise.all(
            associations.map(async (assoc) => {
              // Get the OTHER entity (not the selected account)
              const otherEntityId = assoc.crm_entity_1_id === formData.linked_account_id 
                ? assoc.crm_entity_2_id 
                : assoc.crm_entity_1_id;
              
              try {
                const entityRes = await api.get(`/crm-entities/${otherEntityId}`);
                return {
                  ...assoc,
                  entityName: entityRes.data.system_name
                };
              } catch (error) {
                console.error(`Error fetching entity ${otherEntityId}:`, error);
                return {
                  ...assoc,
                  entityName: 'Unknown Contact'
                };
              }
            })
          );
          
          setFilteredAssociations(enrichedAssociations);
        } catch (error) {
          console.error('Failed to fetch associations:', error);
          setFilteredAssociations([]);
        }
      } else if (!showAccountDropdown) {
        // In Account context, use passed associations
        setFilteredAssociations(associations);
      } else {
        setFilteredAssociations([]);
      }
    };
    fetchAssociationsForAccount();
  }, [formData.linked_account_id, showAccountDropdown]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch contact entries dynamically when an account is selected (Horse context only)
  useEffect(() => {
    if (showAccountDropdown && formData.linked_account_id) {
      api.get(`/contact-entries?crm_entity_id=${formData.linked_account_id}`)
        .then(r => setDynamicContactEntries(r.data || []))
        .catch(() => setDynamicContactEntries([]));
    } else {
      setDynamicContactEntries([]);
    }
  }, [formData.linked_account_id, showAccountDropdown]);

  // When the dialog closes, reset dynamic entries
  useEffect(() => {
    if (!open) setDynamicContactEntries([]);
  }, [open]);

  // Effective contact entries: dynamic in horse context, prop-passed in account context
  const effectiveContactEntries = showAccountDropdown ? dynamicContactEntries : contactEntries;

  // Filter contact entries by communication method
  const filteredContactEntries = (() => {
    const method = formData.communication_method;
    if (method === 'telephone' || method === 'text')                    return effectiveContactEntries.filter(e => e.contact_type === 'phone');
    if (method === 'email_direct' || method === 'email_newsletter')     return effectiveContactEntries.filter(e => e.contact_type === 'email');
    if (method === 'mailer')                                            return effectiveContactEntries.filter(e => e.contact_type === 'address');
    if (method === 'in_person' || method === 'video_conference')        return [];
    return effectiveContactEntries; // 'other' and any unknown → show all
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.activity_name) {
      toast.error('Summary is required');
      return;
    }

    setLoading(true);
    try {
      const dataToSubmit = {
        ...formData,
        activity_type: 'communication',
        crm_entity_id: accountId || formData.linked_account_id || null,
        horse_id: horseId || formData.horse_id || null,
        start_date_time: formData.start_date_time ? new Date(formData.start_date_time).toISOString() : new Date().toISOString(),
      };
      
      await activityAPI.create(dataToSubmit);
      toast.success('Communication logged');
      onSuccess?.();
      onOpenChange(false);
      setFormData({
        activity_name: '',
        description: '',
        communication_method: 'telephone',
        start_date_time: new Date().toISOString().split('T')[0],
        association_id: '',
        horse_id: '',
        assigned_to_user_id: '',
        contact_entry_id: '',
        is_sensitive: false,
        linked_task_ids: [],
        linked_account_id: '',
      });
    } catch (error) {
      console.error('Failed to log communication:', error);
      toast.error('Failed to log communication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="communication-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Log Communication</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="comm-summary">Summary *</Label>
            <Input
              id="comm-summary"
              data-testid="comm-summary-input"
              value={formData.activity_name}
              onChange={(e) => setFormData({ ...formData, activity_name: e.target.value })}
              placeholder="e.g., Discussed upcoming sale"
            />
          </div>

          {/* Conditional Row Layout based on context */}
          {showAccountDropdown ? (
            /* Horse Context: Show Account + Association */
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Linked Account (optional)</Label>
                <Select value={formData.linked_account_id || 'none'} onValueChange={(v) => setFormData({...formData, linked_account_id: v === 'none' ? '' : v, association_id: ''})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.system_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Linked Association (optional)</Label>
                <Select 
                  value={formData.association_id || 'none'} 
                  onValueChange={(val) => setFormData({ ...formData, association_id: val === 'none' ? '' : val })}
                  disabled={!formData.linked_account_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={formData.linked_account_id ? "Select association" : "Select account first"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {filteredAssociations.map(assoc => (
                      <SelectItem key={assoc.id} value={assoc.id}>
                        {assoc.entityName || 'Unknown Contact'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            /* Account Context: Show Association + Horse */
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Linked Association (optional)</Label>
                <Select 
                  value={formData.association_id || 'none'} 
                  onValueChange={(val) => setFormData({ ...formData, association_id: val === 'none' ? '' : val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select association" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {filteredAssociations.map(assoc => (
                      <SelectItem key={assoc.id} value={assoc.id}>
                        {assoc.entityName} - {assoc.relationship?.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Linked Horse (optional)</Label>
                <Select 
                  value={formData.horse_id || 'none'} 
                  onValueChange={(val) => setFormData({ ...formData, horse_id: val === 'none' ? '' : val })}
                >
                  <SelectTrigger data-testid="comm-horse-select">
                    <SelectValue placeholder="Select horse" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {horses.map(horse => (
                      <SelectItem key={horse.id} value={horse.id}>
                        {horse.system_name || horse.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Method</Label>
              <Select 
                value={formData.communication_method} 
                onValueChange={(val) => {
                  // Reset contact_entry_id if the current selection won't be valid for the new method
                  const newMethodTypes = {
                    telephone: 'phone', text: 'phone',
                    email_direct: 'email', email_newsletter: 'email',
                    mailer: 'address',
                    in_person: null, video_conference: null,
                  };
                  const allowedType = newMethodTypes[val]; // undefined = all allowed; null = none allowed
                  const currentEntry = effectiveContactEntries.find(e => e.id === formData.contact_entry_id);
                  const keepEntry = allowedType === undefined
                    ? true                                               // 'other' → keep anything
                    : allowedType !== null && currentEntry?.contact_type === allowedType; // match required
                  setFormData({
                    ...formData,
                    communication_method: val,
                    contact_entry_id: keepEntry ? formData.contact_entry_id : '',
                  });
                }}
              >
                <SelectTrigger data-testid="comm-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_person">In Person</SelectItem>
                  <SelectItem value="telephone">Telephone</SelectItem>
                  <SelectItem value="email_direct">Email (Direct)</SelectItem>
                  <SelectItem value="email_newsletter">Email (Newsletter)</SelectItem>
                  <SelectItem value="video_conference">Video Conference</SelectItem>
                  <SelectItem value="text">Text Message</SelectItem>
                  <SelectItem value="mailer">Mailer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="comm-date">Date</Label>
              <Input
                id="comm-date"
                data-testid="comm-date-input"
                type="date"
                value={formData.start_date_time}
                onChange={(e) => setFormData({ ...formData, start_date_time: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Team Member</Label>
              <Select 
                value={formData.assigned_to_user_id || 'none'} 
                onValueChange={(val) => setFormData({ ...formData, assigned_to_user_id: val === 'none' ? '' : val })}
              >
                <SelectTrigger data-testid="comm-team-member-select">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Contact Detail Used</Label>
              <Select 
                value={formData.contact_entry_id || 'none'} 
                onValueChange={(val) => setFormData({ ...formData, contact_entry_id: val === 'none' ? '' : val })}
              >
                <SelectTrigger data-testid="comm-contact-select">
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {filteredContactEntries.map(entry => (
                    <SelectItem key={entry.id} value={entry.id}>
                      {entry.contact_type === 'email' && entry.email_address}
                      {entry.contact_type === 'phone' && entry.phone_number}
                      {entry.contact_type === 'address' && `${entry.street}, ${entry.city}`}
                      {entry.nickname && ` (${entry.nickname})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="comm-notes">Notes</Label>
            <Textarea
              id="comm-notes"
              data-testid="comm-notes-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Key discussion points..."
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Linked Tasks (optional)</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => setShowTaskDialog(true)}
              >
                Create New
              </Button>
            </div>
            <div className="border rounded p-3 max-h-32 overflow-y-auto space-y-2">
              {tasks.length === 0 ? (
                <p className="text-xs text-muted-foreground">No tasks available</p>
              ) : (
                tasks.slice(0, 10).map(task => (
                  <div key={task.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`task-${task.id}`}
                      checked={(formData.linked_task_ids || []).includes(task.id)}
                      onChange={(e) => {
                        const currentIds = formData.linked_task_ids || [];
                        if (e.target.checked) {
                          setFormData({ ...formData, linked_task_ids: [...currentIds, task.id] });
                        } else {
                          setFormData({ ...formData, linked_task_ids: currentIds.filter(id => id !== task.id) });
                        }
                      }}
                      className="rounded"
                    />
                    <Label htmlFor={`task-${task.id}`} className="text-sm cursor-pointer flex-1">
                      {task.activity_name}
                    </Label>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="comm-sensitive"
              checked={formData.is_sensitive}
              onChange={(e) => setFormData({ ...formData, is_sensitive: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="comm-sensitive" className="text-sm cursor-pointer">
              Mark as sensitive
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="comm-submit-btn">
              {loading ? 'Saving...' : 'Log Communication'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      
      {/* Nested Task Dialog */}
      {showTaskDialog && (
        <TaskDialog
          open={showTaskDialog}
          onOpenChange={setShowTaskDialog}
          accountId={accountId}
          associations={associations}
          users={users}
          communications={[]}
          contactEntries={contactEntries}
          onSuccess={() => {
            setShowTaskDialog(false);
            onSuccess?.(); // Refresh parent data
          }}
        />
      )}
    </Dialog>
  );
};

